import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FailedRequestRecord, readFailedRequest } from "./lastFailedRequest";
import ReportProblemModal from "./ReportProblemModal";
import {
  ReportProblemSource,
  subscribeToReportProblem,
} from "./reportProblemBus";

/**
 * Owns the Report a problem dialog's open state (AKL-798).
 *
 * Mounted once from `App.tsx`, beside `<Header />` — **not** from `Header`, which
 * short-circuits to a logo-only skeleton while the profile is still loading, so a dialog
 * owned by Header would be unreachable exactly when things are slow or broken, which is when
 * people report problems. Its position in `App.tsx` is inside the router and
 * `ProfileProvider`, so the dialog's `useLocation` and `useProfile` work, and it makes the
 * dialog present on `/unexpectedError` and `/unauthorized` too, since both are plain routes
 * inside the same `<Routes>`.
 *
 * While a report is open it ignores further open requests, so the dialog always describes the
 * one incident it was opened for. It renders nothing while closed.
 */
const ReportProblemHost: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [source, setSource] = useState<ReportProblemSource>("menu");
  const [failedRequest, setFailedRequest] =
    useState<FailedRequestRecord | null>(null);
  // Carried from the bus rather than read in the dialog: by the time the dialog's open effect
  // runs, the menu item that was clicked has been unmounted and `document.activeElement` is
  // `<body>`, so the focus restore on close had nothing to restore to (AKL-798).
  const [opener, setOpener] = useState<HTMLElement | null>(null);
  /**
   * Mirrors `isOpen` for the bus subscriber, which is registered once and would otherwise
   * close over the `false` from first render. Written synchronously on both edges rather than
   * in an effect, so two open requests in one tick cannot both get through.
   */
  const isOpenRef = useRef(false);

  useEffect(() => {
    // Read the stored failure at open, not at mount: the record is written by the axios
    // interceptor, which may not have run yet when this mounts.
    //
    // A request that brings its own failure wins over the stored one. That is the toast
    // entry point: the stored record is keep-first, so with two failures on screen the
    // second toast's click would otherwise attach the first one's request, while the toast
    // is showing the second one's message. `undefined` means "no opinion, use the store";
    // an explicit `null` means "this entry point knows there is no failure".
    return subscribeToReportProblem((request) => {
      // First request wins while the dialog is open. Toasts paint above the dialog
      // (`--toastify-z-index: 9999` over its `z-[1400]`), so a second "Report this" is
      // clickable mid-report — and taking it would swap in the new failure while the dialog's
      // capture effect, keyed on `isOpen` alone, kept the first incident's page, time and
      // whatever the user had already typed. That report would describe neither failure.
      // Restarting instead would be truthful but would discard their text, so the open report
      // is left alone; the click still dismisses its own toast, so it is not silent.
      if (isOpenRef.current) return;
      setSource(request.source);
      setOpener(request.opener);
      setFailedRequest(
        request.failedRequest !== undefined
          ? request.failedRequest
          : readFailedRequest(),
      );
      isOpenRef.current = true;
      setIsOpen(true);
    });
  }, []);

  return (
    <ReportProblemModal
      isOpen={isOpen}
      source={source}
      failedRequest={failedRequest}
      opener={opener}
      onClose={() => {
        isOpenRef.current = false;
        setIsOpen(false);
      }}
      onSent={() => toast.success(t("Thanks — your report was sent"))}
    />
  );
};

export default ReportProblemHost;
